// AdminManager.jsx - Gestion des administrateurs avec licences - VERSION TAILWIND NATIF
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';

export default function AdminManager() {
    const { token } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [licences, setLicences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [showPassword, setShowPassword] = useState(false);
    const [showEditPassword, setShowEditPassword] = useState(false);
    
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalPages: 0,
        totalElements: 0
    });

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetPasswordData, setResetPasswordData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showLicenceModal, setShowLicenceModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [selectedLicenceId, setSelectedLicenceId] = useState('');

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        prenom: '',
        nom: '',
        email: '',
        phone: '',
        role: 'ADMIN',
        actif: true,
        licenceId: ''
    });

    const fetchAdmins = useCallback(async (page = 0) => {
        if (!token) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const url = `${APP_CONFIG.BASE_URL}/api/v1/config/admins?page=${page}&size=${pagination.size}`;
            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                if (response.status === 401) throw new Error('Session expirée - Veuillez vous reconnecter');
                if (response.status === 403) throw new Error('Accès refusé - Permissions insuffisantes');
                throw new Error(`Erreur HTTP ${response.status}`);
            }

            const data = await response.json();
            
            if (data.data && Array.isArray(data.data)) {
                setAdmins(data.data);
                setPagination(prev => ({
                    ...prev,
                    page: page,
                    totalElements: data.data.length,
                    totalPages: Math.ceil(data.data.length / prev.size)
                }));
            } else if (Array.isArray(data)) {
                setAdmins(data);
                setPagination(prev => ({
                    ...prev,
                    page: page,
                    totalElements: data.length,
                    totalPages: Math.ceil(data.length / prev.size)
                }));
            } else if (data.content && Array.isArray(data.content)) {
                setAdmins(data.content);
                setPagination({
                    page: data.number || 0,
                    size: data.size || 10,
                    totalPages: data.totalPages || 0,
                    totalElements: data.totalElements || 0
                });
            } else {
                setAdmins([]);
            }
        } catch (err) {
            console.error("Erreur API AdminManager:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, pagination.size]);

    const fetchLicences = useCallback(async () => {
        if (!token) return;

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/licences`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.status === 404) {
                console.warn("Endpoint licences non disponible");
                setLicences([]);
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setLicences(data.data || data || []);
            }
        } catch (err) {
            console.error("Erreur récupération licences:", err);
            setLicences([]);
        }
    }, [token]);

    useEffect(() => {
        fetchAdmins();
        fetchLicences();
    }, [fetchAdmins, fetchLicences]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/superadmin/admins`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                setShowModal(false);
                resetForm();
                fetchAdmins(pagination.page);
                alert("✅ Administrateur créé avec succès");
            } else {
                setError(data.error || "Erreur lors de la création");
                alert(`❌ ${data.error || "Erreur lors de la création"}`);
            }
        } catch (err) {
            console.error("Erreur création:", err);
            setError("Erreur technique lors de la création");
            alert("❌ Erreur technique lors de la création");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const updateData = { ...formData };
            if (!updateData.password) {
                delete updateData.password;
            }

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/admins/${editingAdmin.id}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(updateData)
            });

            const data = await response.json();

            if (response.ok) {
                setShowEditModal(false);
                setEditingAdmin(null);
                resetForm();
                fetchAdmins(pagination.page);
                alert("✅ Administrateur modifié avec succès");
            } else {
                setError(data.error || "Erreur lors de la modification");
                alert(`❌ ${data.error || "Erreur lors de la modification"}`);
            }
        } catch (err) {
            console.error("Erreur modification:", err);
            setError("Erreur technique lors de la modification");
            alert("❌ Erreur technique lors de la modification");
        } finally {
            setLoading(false);
        }
    };

    const handleAssignLicence = async () => {
        if (!selectedLicenceId) {
            alert("Veuillez sélectionner un modèle de licence");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/config/admins/${selectedAdmin.id}/licence?licenceId=${selectedLicenceId}`,
                {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            const data = await response.json();

            if (response.ok) {
                setShowLicenceModal(false);
                setSelectedAdmin(null);
                setSelectedLicenceId('');
                fetchAdmins(pagination.page);
                alert("✅ Licence attribuée avec succès");
            } else {
                setError(data.error || "Erreur lors de l'attribution");
                alert(`❌ ${data.error || "Erreur lors de l'attribution"}`);
            }
        } catch (err) {
            console.error("Erreur attribution licence:", err);
            setError("Erreur technique lors de l'attribution");
            alert("❌ Erreur technique lors de l'attribution");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (admin) => {
        const newStatus = !admin.actif;
        const action = newStatus ? 'activer' : 'désactiver';
        
        if (!window.confirm(`Êtes-vous sûr de vouloir ${action} le compte de ${admin.username || admin.email} ?`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/config/admins/${admin.id}/status?actif=${newStatus}`,
                {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            const data = await response.json();

            if (response.ok) {
                fetchAdmins(pagination.page);
                alert(`✅ Compte ${action} avec succès`);
            } else {
                setError(data.error || `Erreur lors de l'${action}`);
                alert(`❌ ${data.error || `Erreur lors de l'${action}`}`);
            }
        } catch (err) {
            console.error("Erreur changement statut:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (admin) => {
        if (admin.actif !== false) {
            alert("⚠️ Impossible de supprimer un compte actif. Veuillez d'abord le désactiver.");
            return;
        }

        if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir supprimer définitivement le compte de ${admin.username || admin.email} ?\nCette action est irréversible.`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/superadmin/admins/${admin.id}`,
                {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Accept": "application/json"
                    }
                }
            );

            const data = await response.json();

            if (response.ok) {
                fetchAdmins(pagination.page);
                alert("✅ Administrateur supprimé avec succès");
            } else {
                setError(data.error || "Erreur lors de la suppression");
                alert(`❌ ${data.error || "Erreur lors de la suppression"}`);
            }
        } catch (err) {
            console.error("Erreur suppression:", err);
            setError("Erreur technique lors de la suppression");
            alert("❌ Erreur technique lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
            alert("❌ Les mots de passe ne correspondent pas");
            return;
        }

        if (resetPasswordData.newPassword.length < 6) {
            alert("❌ Le mot de passe doit contenir au moins 6 caractères");
            return;
        }

        if (!window.confirm(`Confirmer la réinitialisation du mot de passe pour ${resetEmail} ?`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/auth/reset-password`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ 
                    email: resetEmail,
                    newPassword: resetPasswordData.newPassword
                })
            });

            const data = await response.json();

            if (response.ok) {
                setShowResetModal(false);
                setResetEmail('');
                setResetPasswordData({ newPassword: '', confirmPassword: '' });
                alert(`✅ Mot de passe réinitialisé avec succès`);
            } else {
                setError(data.error || "La réinitialisation a échoué");
                alert(`❌ ${data.error || "La réinitialisation a échoué"}`);
            }
        } catch (err) {
            console.error("Erreur reset:", err);
            setError("Erreur technique lors de la réinitialisation");
            alert("❌ Erreur technique lors de la réinitialisation");
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (admin) => {
        setEditingAdmin(admin);
        setFormData({
            username: admin.username || '',
            password: '',
            prenom: admin.prenom || '',
            nom: admin.nom || '',
            email: admin.email || '',
            phone: admin.phone || '',
            role: admin.role || 'ADMIN',
            actif: admin.actif !== undefined ? admin.actif : true,
            licenceId: admin.licenceId || ''
        });
        setShowEditPassword(false);
        setShowEditModal(true);
    };

    const openResetModal = (email) => {
        setResetEmail(email);
        setResetPasswordData({ newPassword: '', confirmPassword: '' });
        setShowResetModal(true);
    };

    const openLicenceModal = (admin) => {
        setSelectedAdmin(admin);
        setSelectedLicenceId(admin.licenceId ? admin.licenceId.toString() : '');
        setShowLicenceModal(true);
    };

    const resetForm = () => {
        setFormData({
            username: '',
            password: '',
            prenom: '',
            nom: '',
            email: '',
            phone: '',
            role: 'ADMIN',
            actif: true,
            licenceId: ''
        });
        setError(null);
        setShowPassword(false);
        setShowEditPassword(false);
    };

    const changePage = (newPage) => {
        if (newPage >= 0 && newPage < pagination.totalPages) {
            fetchAdmins(newPage);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleResetInputChange = (e) => {
        const { name, value } = e.target;
        setResetPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getStatusBadge = (actif) => {
        if (actif === undefined || actif === null) return null;
        return actif ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Actif
            </span>
        ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Inactif
            </span>
        );
    };

    const formatRole = (role) => {
        if (!role) return 'Admin';
        return role.replace('ROLE_', '');
    };

    const getLicenceName = (licenceId) => {
        if (!licenceId) return 'Aucune';
        const licence = licences.find(l => l.id === licenceId);
        return licence ? licence.modellicence || licence.nom : 'Inconnue';
    };

    if (!token) {
        return (
            <div className="p-6 text-center font-serif">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <p className="text-gray-700 font-serif">Authentification requise pour gérer les administrateurs</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-8xl mx-auto space-y-4 text-left font-serif">
            {/* En-tête */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 pb-4">
                <div>
                    <h2 className="text-2xl font-serif font-bold text-gray-900 tracking-tight">
                        Gestion des Administrateurs
                    </h2>
                    <p className="text-sm text-gray-500 font-serif">
                        Création, gestion et attribution des licences
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Ajouter
                    </button>
                </div>
            </div>

            {/* Message d'erreur */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700"><strong>Erreur :</strong> {error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tableau des administrateurs */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur / Nom</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email / Téléphone</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Licence</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading && admins.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center">
                                        <div className="flex justify-center">
                                            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2 font-serif">Chargement...</p>
                                    </td>
                                </tr>
                            ) : admins.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center">
                                        <p className="text-sm text-gray-400 font-serif">Aucun administrateur trouvé</p>
                                    </td>
                                </tr>
                            ) : (
                                admins.map(admin => (
                                    <tr key={admin.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-serif font-bold text-gray-900">
                                                {admin.username || admin.email}
                                            </div>
                                            <div className="text-[11px] font-serif text-gray-500">
                                                {admin.prenom} {admin.nom || ''}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-serif text-gray-700">
                                                {admin.email}
                                            </div>
                                            <div className="text-[11px] font-serif text-gray-500 font-mono">
                                                {admin.phone || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${admin.role === 'SUPERADMIN' || admin.role === 'ROLE_SUPERADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                                {formatRole(admin.role)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                {getLicenceName(admin.licenceId)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {getStatusBadge(admin.actif)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-1 flex-wrap">
                                                {/* Bouton Licence */}
                                                <button
                                                    onClick={() => openLicenceModal(admin)}
                                                    className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                                    title="Attribuer une licence"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                                    </svg>
                                                </button>

                                                {/* Bouton Modifier */}
                                                <button
                                                    onClick={() => openEditModal(admin)}
                                                    className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                                    title="Modifier"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                    </svg>
                                                </button>

                                                {/* Bouton Réinitialiser mot de passe */}
                                                <button
                                                    onClick={() => openResetModal(admin.email)}
                                                    className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                                    title="Réinitialiser le mot de passe"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                </button>

                                                {/* Bouton Activer/Désactiver */}
                                                <button
                                                    onClick={() => handleToggleStatus(admin)}
                                                    className={`p-1 rounded-md ${admin.actif ? 'text-yellow-600 hover:text-yellow-800' : 'text-green-600 hover:text-green-800'} hover:bg-gray-100`}
                                                    title={admin.actif ? 'Désactiver' : 'Activer'}
                                                >
                                                    {admin.actif ? (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    )}
                                                </button>

                                                {/* Bouton Supprimer */}
                                                <button
                                                    onClick={() => handleDelete(admin)}
                                                    className={`p-1 rounded-md ${admin.actif !== false ? 'opacity-50 cursor-not-allowed text-gray-300' : 'text-red-600 hover:text-red-800 hover:bg-gray-100'}`}
                                                    title={admin.actif !== false ? 'Désactivez d\'abord le compte' : 'Supprimer'}
                                                    disabled={admin.actif !== false}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <span className="text-xs font-serif text-gray-500">
                            {pagination.totalElements} administrateur(s) - Page {pagination.page + 1}/{pagination.totalPages}
                        </span>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => changePage(pagination.page - 1)}
                                disabled={pagination.page === 0 || loading}
                                className="px-3 py-1 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                ← Précédent
                            </button>
                            <button
                                onClick={() => changePage(pagination.page + 1)}
                                disabled={pagination.page + 1 >= pagination.totalPages || loading}
                                className="px-3 py-1 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Suivant →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ======================================== */}
            {/* MODAL : CRÉATION D'UN ADMINISTRATEUR */}
            {/* ======================================== */}
            {showModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <div 
                        className="fixed inset-0 bg-black/50" 
                        onClick={() => { setShowModal(false); resetForm(); }}
                    ></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
                                <h3 className="text-xl font-serif font-bold text-gray-900 flex items-center gap-2">
                                    <span className="text-blue-600">✦</span>
                                    Nouvel administrateur
                                </h3>
                                <button 
                                    onClick={() => { setShowModal(false); resetForm(); }} 
                                    className="text-gray-400 hover:text-gray-600 text-2xl"
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleCreate} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Identifiant <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="username"
                                        placeholder="Nom d'utilisateur"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        autoComplete="username"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Mot de passe <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Mot de passe"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Prénom
                                        </label>
                                        <input
                                            type="text"
                                            name="prenom"
                                            placeholder="Prénom"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={formData.prenom}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nom <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="nom"
                                            placeholder="Nom"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={formData.nom}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="exemple@domain.com"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        autoComplete="email"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Téléphone
                                    </label>
                                    <input
                                        type="text"
                                        name="phone"
                                        placeholder="XX XX XX XX XX"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        autoComplete="tel"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Rôle
                                    </label>
                                    <select
                                        name="role"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                    >
                                        <option value="ADMIN">Admin Standard</option>
                                        <option value="SUPERADMIN">Super Admin</option>
                                    </select>
                                </div>

                                <div className="flex items-center gap-3 pt-2">
                                    <input
                                        type="checkbox"
                                        name="actif"
                                        id="actif"
                                        checked={formData.actif}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label htmlFor="actif" className="text-sm font-serif text-gray-700">
                                        Activer le compte immédiatement
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-700 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? 'Création...' : 'Créer'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : MODIFICATION */}
            {/* ======================================== */}
            {showEditModal && editingAdmin && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <div 
                        className="fixed inset-0 bg-black/50" 
                        onClick={() => { setShowEditModal(false); setEditingAdmin(null); resetForm(); }}
                    ></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
                                <h3 className="text-xl font-serif font-bold text-gray-900 flex items-center gap-2">
                                    <span className="text-blue-600">✎</span>
                                    Modifier le compte
                                </h3>
                                <button 
                                    onClick={() => { setShowEditModal(false); setEditingAdmin(null); resetForm(); }} 
                                    className="text-gray-400 hover:text-gray-600 text-2xl"
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleUpdate} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Identifiant
                                    </label>
                                    <input
                                        type="text"
                                        name="username"
                                        value={formData.username || ''}
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed shadow-sm"
                                    />
                                    <p className="text-xs text-gray-400 mt-1 italic font-serif">
                                        L'identifiant ne peut pas être modifié
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nouveau mot de passe
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showEditPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Laisser vide pour ne pas changer"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                                            value={formData.password || ''}
                                            onChange={handleInputChange}
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowEditPassword(!showEditPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            tabIndex={-1}
                                        >
                                            {showEditPassword ? (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1 italic font-serif">
                                        Laissez vide pour conserver le mot de passe actuel
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Prénom
                                        </label>
                                        <input
                                            type="text"
                                            name="prenom"
                                            placeholder="Prénom"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={formData.prenom || ''}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nom <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="nom"
                                            placeholder="Nom"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={formData.nom || ''}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email || ''}
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed shadow-sm"
                                    />
                                    <p className="text-xs text-gray-400 mt-1 italic font-serif">
                                        L'email ne peut pas être modifié
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Téléphone
                                    </label>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone || ''}
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed shadow-sm"
                                    />
                                    <p className="text-xs text-gray-400 mt-1 italic font-serif">
                                        Le téléphone ne peut pas être modifié
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Rôle
                                    </label>
                                    <select
                                        name="role"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={formData.role || 'ADMIN'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="ROLE_ADMIN">Admin Standard</option>
                                        <option value="ROLE_SUPERADMIN">Super Admin</option>
                                    </select>
                                </div>

                                <div className="flex items-center gap-3 pt-2">
                                    <input
                                        type="checkbox"
                                        name="actif"
                                        id="editActif"
                                        checked={formData.actif !== undefined ? formData.actif : true}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label htmlFor="editActif" className="text-sm font-serif text-gray-700">
                                        Compte actif
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setEditingAdmin(null);
                                            resetForm();
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-700 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? 'Mise à jour...' : 'Mettre à jour'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : ATTRIBUTION DE LICENCE */}
            {/* ======================================== */}
            {showLicenceModal && selectedAdmin && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <div 
                        className="fixed inset-0 bg-black/50" 
                        onClick={() => { setShowLicenceModal(false); setSelectedAdmin(null); setSelectedLicenceId(''); }}
                    ></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-serif font-bold text-gray-900">
                                    Attribution d'une licence
                                </h3>
                                <p className="text-sm font-serif text-gray-500 mt-1">
                                    Attribuer un modèle de licence à :
                                </p>
                                <p className="text-sm font-serif font-bold text-gray-900 mt-1">
                                    {selectedAdmin.username || selectedAdmin.email}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Modèle de licence <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={selectedLicenceId}
                                        onChange={(e) => setSelectedLicenceId(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Sélectionner un modèle</option>
                                        {licences.filter(l => l.actif || l.status).map(licence => (
                                            <option key={licence.id} value={licence.id}>
                                                {licence.modellicence || licence.nom} - {licence.maxEtudiants} étudiants
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedLicenceId && (
                                    <div className="bg-gray-50 rounded-xl p-3 text-xs font-serif text-gray-500">
                                        {licences.find(l => l.id === parseInt(selectedLicenceId))?.description}
                                    </div>
                                )}

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowLicenceModal(false);
                                            setSelectedAdmin(null);
                                            setSelectedLicenceId('');
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={handleAssignLicence}
                                        disabled={loading || !selectedLicenceId}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-700 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? 'Attribution...' : 'Attribuer'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : RÉINITIALISATION MOT DE PASSE */}
            {/* ======================================== */}
            {showResetModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <div 
                        className="fixed inset-0 bg-black/50" 
                        onClick={() => { setShowResetModal(false); setResetEmail(''); setResetPasswordData({ newPassword: '', confirmPassword: '' }); }}
                    ></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-serif font-bold text-gray-900">
                                    Réinitialisation du mot de passe
                                </h3>
                                <p className="text-sm font-serif text-gray-500 mt-1">
                                    Définir un nouveau mot de passe pour :
                                </p>
                                <p className="text-sm font-serif font-bold text-gray-900 mt-1">
                                    {resetEmail}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nouveau mot de passe <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        placeholder="Nouveau mot de passe (min 6 caractères)"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                        value={resetPasswordData.newPassword}
                                        onChange={handleResetInputChange}
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirmer le mot de passe <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        placeholder="Confirmer le mot de passe"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                        value={resetPasswordData.confirmPassword}
                                        onChange={handleResetInputChange}
                                        autoComplete="new-password"
                                    />
                                </div>

                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                    <p className="text-xs font-serif text-yellow-800">
                                        ⚠️ L'utilisateur devra utiliser ce nouveau mot de passe pour se connecter.
                                    </p>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowResetModal(false);
                                            setResetEmail('');
                                            setResetPasswordData({ newPassword: '', confirmPassword: '' });
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={handleResetPassword}
                                        disabled={loading}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-700 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? 'Envoi...' : 'Réinitialiser'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}